import Vue from 'vue';
import Component from 'vue-class-component';
import {Mixins, Ref} from 'vue-property-decorator';
import {_HOMEWORKS_STORE} from '@/homework/values/homeworks.values';
import {SHOW_DISCONNECTED_MODAL_EVENT} from '@/core/values/app.values';
import TaskWhiteboardMixin from '@/mixins/common/task-whiteboard.mixin';
import EdWhiteboardMenu
    from '@/core/components/ed-meeting-transparency/ed-whiteboard-menu/ed-whiteboard-menu.component.vue';
import {BOARD_EVENTS, ToolIconType} from '@/values/ed-whiteboard.values';
import {BoardType} from '@/components/common/ed-board/ed-board.enum';
import BoardPage from '@/homework/models/board-page.model';
import KEYS from '@/values/keys';
import {MEETING_EVENTS} from '@/meetings/values/meetings.values';
import WboDocument from '../ed-board/ed-board-tools/wbo-client/model/wbo-document';
import { WboVersionedAnnotationData } from '../ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import WboClient from '../ed-board/ed-board-tools/wbo-client/wbo-client';
import { ConflictResolutionMethod } from '../ed-board/ed-board-tools/wbo-client/values/wbo-client.values';
import { EdBoardV2 } from '../ed-board/ed-board-v2.component.vue';

@Component({
    components: {
        'EdBoardV2': EdBoardV2,
        EdWhiteboardMenu
    }

})
class EdBlankWhiteboard extends Mixins(TaskWhiteboardMixin) {
    @Ref() readonly board;
    declare readonly boardMenu;

    get currentMeetingData() {
        return this.$store.state.MeetingsStore.currentMeetingData;
    }

    get isPublisher() : boolean {
        return this.$store.state.AuthStore.role === KEYS.ROLES.PUBLISHER;
    }

    get isStudentToggle() : boolean {
        return this.currentMeetingData.studentToggle;
    }

    get isStudent(): boolean {
        return !this.isPublisher && !this.isPresentationModeGuestTeacher;
    }

    get isReadOnlyStudent(): boolean {
        return (this.isStudent || this.isPresentationModeGuestTeacher) && this.isStudentToggle;
    }

    private boardPage: BoardPage = {
        page: 1,
        width: 2000,
        height: 2000,
        boardType: BoardType.Whiteboard
    };

    /**
     * Passed to the ed-whiteboard-menu component.
     */
    private customMenuTools: ToolIconType = {
        zoom: {icon: 'search-solid', refId: 'toolID-Zoom', i18n: 'drawItems.zoom' , name: 'Zoom'}
    }

    private showLoadingOverlay(): void {
        this.showLoading();
        this.boardMenu?.disableUndoOrRedoOptions();
    }

    private initBoard() {
        this.boardPage.width = Math.max(this.boardPage.width, this.pageContainer.clientWidth);
        this.boardPage.height = Math.max(this.boardPage.height, this.pageContainer.clientHeight);

        this.$edEventBus.$emit(`${this.boardGroupHash}_${this.boardPage.page}${BOARD_EVENTS.CREATE_BOARD}`);

        this.$edEventBus.$emit(`${BOARD_EVENTS.CREATE_BOARD_PAGE}${this.boardPage.page}`, true);
        this.$edEventBus.$emit(`${BOARD_EVENTS.ADD_CLASS_CURRENT_PAGE_}${this.boardPage.page}`);
    }

    /**
     * This one gets called when the child EdBoard finished its initialization, and it's ready to receive events.
     */
    public finishBoardInit(): void {
        this.initBoard();
    }

    public onBoardLoaded(pageNumber: number) {
        this.boardMenu?.resetIcons();
        this.setDefaultColor();
        this.finishLoading();
    }

    public handleMessage(message: any): void {
        // EdBoard already processed this to render the incoming messages
        // We are interested in some incoming messages to control the rendering of the list of connected users to the
        // blank whiteboard.
        if (message.tool == 'Cursor' && message.type == 'update') {
            this.$edEventBus.$emit(BOARD_EVENTS.CURSOR_UPDATED, message);
        }
    }

    /**
     * Handler for the 'go-to-user-cursor' event emitted by the ed-blank-whiteboard-users component.
     */
    public goToUserCursor(cursorId: string): void {
        let userCursor = document.querySelector(`[id^="${cursorId}"]`);
        if (userCursor) {
            let position = userCursor.getBoundingClientRect();
            let xCenterPosition = this.pageContainer.clientWidth / 2;
            let yCenterPosition = this.pageContainer.clientHeight / 2;
            let left = position.width / 2 + position.left + this.pageContainer.scrollLeft - xCenterPosition;
            let top = position.top + this.pageContainer.scrollTop - yCenterPosition;
            this.pageContainer.scrollTo({left, top, behavior: 'smooth'});
        }
    }

    public onStudentReadonlyToggle() {
        if (this.isReadOnlyStudent) {
            this.boardMenu.activateTool('Hand', 'move', false);
        }
    }

    onAnnotationConflict(localDocument: WboDocument, conflictingChange: WboVersionedAnnotationData, instance: WboClient) {
        instance?.resolveConflict(ConflictResolutionMethod.Merge);
    }

    onWboClientCreated(instance: WboClient) {
        instance?.initBoard({
            isTransparency: false,
            width: this.boardPage.width,
            height: this.boardPage.height,
        });
    }

    mounted() {
        this.$edEventBus.$on(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.onBoardLoaded);
        this.$edEventBus.$on(BOARD_EVENTS.DRAWING_TASK, this.registerDrawing);
        this.$edEventBus.$on(BOARD_EVENTS.INIT_BOARD_TASK, this.finishBoardInit);
        this.$edEventBus.$on(this.boardGroupHash + '_1', this.handleMessage);
        this.$edEventBus.$on(MEETING_EVENTS.SET_READ_ONLY_STUDENT, this.onStudentReadonlyToggle);
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_BOARD_ACTIVE, true);
        setTimeout(() => {
            this.showLoadingOverlay();
            this.connect();
        });
    }

    beforeDestroy() {
        this.disconnectSocket();
        this.$edEventBus.$off(BOARD_EVENTS.CLOSE_LOADING_BOARD_TASK, this.onBoardLoaded);
        this.$edEventBus.$off(BOARD_EVENTS.DRAWING_TASK, this.registerDrawing);
        this.$edEventBus.$off(BOARD_EVENTS.INIT_BOARD_TASK, this.finishBoardInit);
        this.$edEventBus.$off(this.boardGroupHash + '_1', this.handleMessage);
        this.$store.commit(_HOMEWORKS_STORE.MUTATIONS.SET_BOARD_ACTIVE, false);
        if (this.isPreviousDisconnected || this.isPreviousOffline) {
            this.$EdToast.hide();
            this.$edEventBus.$emit(SHOW_DISCONNECTED_MODAL_EVENT, this.isPreviousDisconnected);
        }
    }
}

export default Vue.component('ed-blank-whiteboard', EdBlankWhiteboard);